import { normaliseTempo, Section, SectionOutbound } from "./model";

/**
 * Conversion between the in-memory shape (a Cell is `string[]`) and the stored
 * shape (a Cell is one comma-separated string).
 *
 * Firestore rejects nested arrays, which is the entire reason this exists.
 *
 * KNOWN LIMITATION (pre-existing, deliberately preserved): a subcell containing
 * a comma would be split into two on the next load. Sargam-encoded tracks can
 * never contain a comma, but a free-text "simple" track could. Fixing it means
 * changing the stored format, so it is out of scope for the refactor and is
 * tracked separately.
 */

/**
 * Stored → in-memory: split each comma-separated cell back into subcells.
 *
 * TOTAL BY DESIGN. This runs on documents read straight out of storage, and it
 * is now reachable from a server route — so a single malformed document must
 * degrade to an empty structure rather than throw and take a whole page (or a
 * whole dashboard listing) down with it. Anything that is not the shape we
 * expect is dropped, never dereferenced.
 */
export function parseSections(
    sections: Array<SectionOutbound | Section> | unknown
): Section[] {
    if (!Array.isArray(sections)) return [];

    return sections
        .filter((section): section is Section => Boolean(section) && typeof section === "object")
        .map((section) => {
            const rows = Array.isArray((section as Section).rows)
                ? (section as Section).rows
                : [];
            return {
                ...section,
                name: typeof (section as Section).name === "string"
                    ? (section as Section).name
                    : "",
                // Section tempo is groundwork: nothing reads it yet, and the
                // spread above would already carry it through. It is coerced
                // anyway for the same reason `name` is — a value the save
                // schema refuses would load fine and then make the document
                // permanently unsaveable, which is exactly how the row
                // parallel-array bug presented (a permanent 400 on every
                // autosave, with the session's work never persisted).
                //
                // The key is written only when it was already present, so no
                // existing document grows a `tempo` field on its next save.
                ...("tempo" in (section as Section)
                    ? { tempo: normaliseTempo((section as Section).tempo) }
                    : {}),
                rows: rows
                    .filter((row) => Boolean(row) && typeof row === "object")
                    .map((row) => {
                        const vectors = Array.isArray(row.vectors) ? row.vectors : [];
                        return {
                            ...row,
                            vectors: vectors
                                .filter((v) => Boolean(v) && typeof v === "object")
                                .map((vector) => {
                                    const cells = Array.isArray(vector.cells)
                                        ? (vector.cells as Array<string | string[]>)
                                        : [];
                                    return {
                                        ...vector,
                                        cells: cells.map((cell) =>
                                            typeof cell === "string"
                                                ? cell.split(",")
                                                : Array.isArray(cell)
                                                  ? cell
                                                  : [""]
                                        ),
                                    };
                                }),
                        };
                    }),
            };
        }) as Section[];
}

/**
 * In-memory → stored: join each cell's subcells with commas.
 *
 * Only cells are rewritten. Everything else on a section — `name`, and now
 * `tempo` — rides out through the spread, so a field added to `Section` needs
 * no change here. The place that DOES need one is the save-path zod schema,
 * which strips keys it does not know about.
 */
export function packSections(
    sections: Array<Section | SectionOutbound>
): SectionOutbound[] {
    return sections.map((section) => ({
        ...section,
        rows: section.rows.map((row) => ({
            ...row,
            vectors: row.vectors.map((vector) => ({
                ...vector,
                cells: (vector.cells as Array<string | string[]>).map((cell) =>
                    typeof cell === "string" ? cell : cell.join(",")
                ),
            })),
        })),
    })) as SectionOutbound[];
}
